import { IItem } from '../../timeline/store/timeline.types'
import { IEKGEvent } from '../store/ekgImport.types'
import { IInitiateIvState } from '../../procedures/initiateIv/store/initiateIv.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IOptions } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateInitiateIvProcedureProps {
  procedureEvent: IEKGEvent
  initiateIvOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface InitiateIvProcedureObject {
  timelineItem: IItem
  initiateIvState: IInitiateIvState
}

const createInitiateIvProcedure = (props: CreateInitiateIvProcedureProps): InitiateIvProcedureObject => {
  const { procedureEvent, initiateIvOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const ivOptionId = procedureEvent.procedureAction
  const ivTypeOption = initiateIvOptions.find((option) => option.name === ivOptionId)
  const itemComments = procedureEvent.procedureComments || ''
  const itemSubHeader = `By , Site: , Gauge: , Type: ${
    ivTypeOption?.name || ''
  }, Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.INITIATE_IV_PROCEDURE,
    null,
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    itemType: 'Procedures',
    event: 'Initiate IV',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_INITIATE_IV_PROCEDURE_ITEM,
    defaultHref: TabConstants.INITIATE_IV_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let initiateIvState: IInitiateIvState = {
    key: clientEventId,
    clientCorrelationId,
    site: null,
    sitePNNV: null,
    size: '',
    iVType: ivTypeOption || null,
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Initiate IV',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    initiateIvDefaults: null,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    initiateIvState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: initiateIvState,
      actionField: (s) => s.iVType?.name,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    initiateIvState = handleNemsisCodeError(error, initiateIvState)
  }

  const initiateIvProcedureObject: InitiateIvProcedureObject = {
    initiateIvState,
    timelineItem,
  }

  return initiateIvProcedureObject
}

export default createInitiateIvProcedure
